import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { heroSlides } from "../data/mock";

export default function HeroCarousel() {
  const [idx, setIdx] = useState(0);
  const count = heroSlides.length;

  const next = useCallback(() => setIdx((i) => (i + 1) % count), [count]);
  const prev = () => setIdx((i) => (i - 1 + count) % count);

  useEffect(() => {
    const t = setInterval(next, 6000);
    return () => clearInterval(t);
  }, [next]);

  return (
    <section className="relative h-[560px] md:h-[640px] overflow-hidden bg-black">
      {heroSlides.map((s, i) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${i === idx ? "opacity-100" : "opacity-0"}`}
        >
          <div
            className="absolute inset-0 bg-cover bg-top scale-105"
            style={{ backgroundImage: `url(${s.image})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/20" />
          <div className="relative max-w-7xl mx-auto h-full px-6 flex items-center">
            <div className="max-w-2xl text-white">
              <p className="font-script text-amber-300 text-2xl md:text-3xl mb-3">{s.eyebrow}</p>
              <h2 className="text-4xl md:text-6xl font-extrabold leading-tight mb-5">
                {s.titleLines.map((line, k) => (
                  <span key={k} className="block">{line}</span>
                ))}
              </h2>
              <div className="flex items-center gap-3 mb-6">
                <span className="h-[2px] w-10 bg-amber-300"></span>
                <span className="text-2xl text-amber-300">♥</span>
                <span className="h-[2px] w-10 bg-amber-300"></span>
              </div>
              <p className="text-lg text-white/85 mb-7 max-w-xl">{s.subtitle}</p>
              <Link
                to={s.ctaLink}
                className="inline-flex items-center gap-2 bg-amber-400 text-[#1a1a1a] hover:bg-amber-300 px-7 py-3.5 rounded-full font-bold shadow-lg transition-colors"
              >
                {s.ctaText}
                <ChevronRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      ))}

      {/* Arrows */}
      <button
        onClick={prev}
        aria-label="Previous slide"
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 hover:bg-white/30 backdrop-blur flex items-center justify-center text-white border border-white/30"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      <button
        onClick={next}
        aria-label="Next slide"
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 hover:bg-white/30 backdrop-blur flex items-center justify-center text-white border border-white/30"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      {/* Dots */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {heroSlides.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            className={`h-2 rounded-full transition-all ${i === idx ? "w-8 bg-amber-400" : "w-2 bg-white/50"}`}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>
    </section>
  );
}
